/**
 * SONG DETAIL — what opens when a song is clicked, in the catalogue and in the Library.
 *
 * THE POINT OF IT IS WHERE IT DOES NOT GO. Clicking a song on the Home page must never
 * open the Studio: the Studio is where music is created and edited, the catalogue is where
 * music is discovered and listened to, and a card that jumped from one to the other would
 * blur the two experiences the product keeps separate. So a card opens this instead, over
 * the page, and the visitor stays exactly where they were — the shared Modal locks
 * background scroll and restores it on unmount, which is what preserves the scroll
 * position for free.
 *
 * TWO SURFACES, ONE DIALOG — `surface`, and Batch 6 §9. The Library's rows raise this same
 * dialog, and §9 asks for a different action row there: no Love button, no Save button, and
 * an explicit "Open in Studio" instead. The same paragraph forbids taking Love and Save away
 * from the other surfaces ("Do not remove Love/Save functionality from other song surfaces"),
 * so the difference has to be a variant of one dialog rather than an edit to it. A second
 * component would be the other option and a worse one: the sheet, the artwork, the metadata,
 * the empty-lyrics sentence and every way out would exist twice and drift apart.
 *
 * `surface='library'` therefore does exactly three things — drops Like, drops Save, adds
 * Open in Studio — and changes nothing else. Note what it does NOT do: the Library's rows
 * keep their own favourite control and their own Add-to-playlist menu item, so liking and
 * saving a library track are still one press away. §9 removes them from THIS VIEW, not from
 * the Library.
 *
 * WHAT IT SHOWS: the cover, the title, the metadata, a rule, and the sheet. The whole
 * sheet, scrolling — this is the one place in the app outside the Studio where the full
 * lyrics belong, which is what makes the one-line excerpts on the cards a glimpse of
 * something rather than the only version of it.
 *
 * THE REAL SHEET OR AN HONEST SENTENCE. `song.lyrics` is what is rendered; when it is
 * empty the dialog says so in words. Never an empty panel, never "undefined", never a
 * heading over nothing — a freshly generated track legitimately has no lyrics, so that
 * case is normal and has to read as normal.
 *
 * WHY THE STRUCTURE IS PRESERVED. A bracketed line — `[Chorus]` — IS a heading in this
 * format, the same convention lib/sections.ts reads for the timeline's markers and
 * studio/LyricsPanel renders in its reading mode. Printing it verbatim as a body line
 * would show the reader markup; picking it out is how it reads as a lyric sheet.
 *
 * THE SHEET ITSELF IS songs/LyricSheet NOW, which is Batch 6 §10. This file used to hold
 * its own `Sheet` and its own copy of the heading regex, and so did studio/LyricsPanel;
 * §10 asks the lyrics to follow playback, which is a behaviour belonging to "the lyric
 * sheet" rather than to one dialog, so the two renderers became one component. `at` is
 * this dialog's contribution to it — the song's share of its length, from the app's one
 * player, and null when this song is not the loaded one. What the highlight does and does
 * NOT claim is documented there.
 *
 * PRESENTATION ONLY, like the other dialogs here. Playing, liking, sharing and opening the
 * Studio are callbacks — AppModals wires them to the app's one player, the songs store, the
 * share link and the router, so there is no second player, no second favourite flag and no
 * second route into the workspace. See ./index.ts for why this file is not exported from the
 * barrel.
 *
 * SIMULATED PLAYBACK, as everywhere: `Song.audioUrl` is null while generation is
 * simulated, so Play advances the shared clock and produces no sound. The Home page says
 * so once, next to the sections, rather than this dialog implying otherwise.
 */
import {
  Bookmark,
  Heart,
  Loader2,
  Maximize2,
  Music,
  Pause,
  Play,
  Share2,
  SlidersHorizontal,
  X,
} from 'lucide-react';

import { Btn, IconButton, Modal } from '../ui';
import { LyricSheet, SongArtwork } from '../songs';
import { joinWithMiddleDot } from '../../lib/formatting';
import { c } from '../../styles/theme';
import type { LyricsSurface, Song } from '../../types/domain';

export interface LyricsModalProps {
  /** The song to preview, or null when the dialog is closed. */
  song: Song | null;
  onClose: () => void;
  /** True when the shared player is running this song. */
  playing: boolean;
  /** Toggle playback. Wire to `usePlayer().toggle`. */
  onTogglePlay: (id: string) => void;
  /** True while playback is starting or buffering for this song. */
  loading?: boolean;
  /** Toggle the song's saved state. Wire to the songs store's `toggleFavorite`. */
  onToggleLike: (id: string) => void;
  /** Add the song to a playlist — the app's existing "save" action. */
  onSave: (song: Song) => void;
  /** Copy or share the song's Lyrixa link. */
  onShare: (song: Song) => void;
  /**
   * Which surface opened this, deciding the action row. Defaults to `'catalogue'`.
   *
   * Optional with a default rather than required, so the four existing call sites and the
   * component tests keep the discovery actions without being edited. See the header and
   * types/domain.LyricsSurface.
   */
  surface?: LyricsSurface;
  /**
   * Opens the song in the Studio workspace. Only rendered when `surface === 'library'`.
   *
   * Optional because the catalogue has no such button by design — a card opening the Studio
   * is the exact thing this dialog exists to prevent. When the Library omits it the button
   * is not rendered rather than rendered dead, so there is no control on screen that does
   * nothing.
   */
  onOpenInStudio?: (song: Song) => void;
  /** Opens this song in the app shell's existing full-screen player. */
  onOpenFullscreen?: (song: Song) => void;
  /**
   * Playback position as a share of this song's length, 0–1 — used by the untimed fallback
   * when this song has no real lyric timings.
   */
  at?: number | null;
  /**
   * Playback position in seconds — used when the song carries real line timings.
   *
   * Optional so the component tests and any future call site can render a static sheet,
   * which is also what a closed player looks like.
   */
  elapsedSec?: number | null;
}

export default function LyricsModal({
  song,
  onClose,
  playing,
  onTogglePlay,
  loading = false,
  onToggleLike,
  onSave,
  onShare,
  surface = 'catalogue',
  onOpenInStudio,
  onOpenFullscreen,
  at = null,
  elapsedSec = null,
}: LyricsModalProps) {
  /*
    Rendered mounted-but-closed like the other dialogs, so `open` drives it rather than a
    conditional in AppModals. The guard is what makes the body below able to read `song`
    without a null check on every line.
  */
  if (!song) return <Modal open={false} onClose={onClose} />;

  const hasLyrics = Boolean(song.lyrics?.trim());

  return (
    <Modal open onClose={onClose} ariaLabel={`${song.title} — lyrics`} width={520}>
      {/* ---- the header: cover, title, metadata, and the way out ----
          The X is here rather than in the shared Modal because it is the only dialog
          in the app with one: the others are decisions with named buttons ("Cancel",
          "Done"), and this is a thing you were looking at. Escape and a click on the
          overlay close it too — both come from Modal — but a visitor browsing a
          catalogue with a mouse should not have to know that. */}
      <div className="lyx-lyrics-head">
        <div className="lyx-lyrics-cover">
          <SongArtwork
            song={song}
            depth="hero"
            radius={10}
            style={{ width: '100%', height: '100%' }}
          >
            <Music
              size={14}
              color="rgba(255,255,255,0.5)"
              style={{ position: 'absolute', bottom: 8, left: 9 }}
              aria-hidden="true"
            />
          </SongArtwork>
        </div>

        <div style={{ minWidth: 0, flex: '1 1 auto' }}>
          <p
            style={{
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 600,
              fontSize: 17,
              margin: 0,
              lineHeight: 1.3,
              color: c.text1,
              overflowWrap: 'anywhere',
            }}
          >
            {song.title}
          </p>
          {/*
            "Made with Lyrixa AI" rather than an artist name, and that is deliberate: a
            Song has no creator field (see types/domain), so any name here would be
            invented. This says what is actually known about who made it.
          */}
          <p style={{ fontSize: 11.5, color: c.text2, margin: '5px 0 0' }}>Made with Lyrixsa AI</p>
          <p style={{ fontSize: 11.5, color: c.text2, margin: '3px 0 0' }}>
            {joinWithMiddleDot([song.genre, song.length])}
          </p>
        </div>

        <IconButton label="Close lyrics" onClick={onClose}>
          <X size={14} color={c.text2} />
        </IconButton>
      </div>

      <hr style={{ border: 'none', borderTop: `1px solid ${c.border}`, margin: '16px 0 14px' }} />

      {hasLyrics ? (
        <LyricSheet
          lyrics={song.lyrics}
          at={at}
          elapsedSec={elapsedSec}
          lyricTimings={song.lyricTimings}
          lyricSectionTimings={song.lyricSectionTimings}
          variant="dialog"
          className="lyx-lyrics-sheet"
        />
      ) : (
        /*
          THE SENTENCE, not an empty box. A track with no sheet yet is an ordinary state,
          so the dialog says which state it is in — the alternative is a rule with nothing
          under it, which reads as a rendering fault.
        */
        <p style={{ fontSize: 12.5, color: c.text2, margin: 0, lineHeight: 1.7 }}>
          Lyrics aren&rsquo;t available for this song yet.
        </p>
      )}

      {/* ---- the actions ----
          Play first and full width on both surfaces: this is a song, and listening is what
          the click was about. What sits under it is where the two surfaces differ — see the
          header, and Batch 6 §9. */}
      <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
        <Btn
          full
          onClick={() => onTogglePlay(song.id)}
          aria-label={`${playing ? 'Pause' : 'Play'} ${song.title}`}
        >
          {loading ? (
            <Loader2 size={14} color="#1A1300" className="lyx-spin" />
          ) : playing ? (
            <Pause size={14} fill="#1A1300" />
          ) : (
            <Play size={14} fill="#1A1300" />
          )}
          {playing ? 'Pause song' : 'Play song'}
        </Btn>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {/*
            LIKE AND SAVE ARE THE CATALOGUE'S, and §9 takes them off the Library's detail
            view. Not `disabled` and not hidden with CSS — absent: a greyed-out Love button
            is a control that looks broken, and this is not a permission, it is a surface
            that does not offer the action. The Library's rows still do.
          */}
          {surface === 'catalogue' && (
            <>
              {/* Like IS the app's favourite flag, not a new counter: `Song.favorite` is the
                  one engagement field that exists, and `aria-pressed` is what makes the two
                  states distinguishable without relying on the icon's fill. */}
              <Btn
                variant="secondary"
                onClick={() => onToggleLike(song.id)}
                aria-pressed={song.favorite}
                aria-label={song.favorite ? `Unlike ${song.title}` : `Like ${song.title}`}
                style={{ flex: '1 1 0', minWidth: 0 }}
              >
                <Heart
                  size={13}
                  color={song.favorite ? c.accent : undefined}
                  fill={song.favorite ? c.accent : 'none'}
                />
                {song.favorite ? 'Liked' : 'Like'}
              </Btn>
              {/* Save opens the existing Add-to-playlist sheet. There is no bookmark concept
                  in the app and inventing one would mean a second, parallel place a song can
                  be kept; a playlist is what "save this" already means here. */}
              <Btn
                variant="secondary"
                onClick={() => onSave(song)}
                aria-label={`Save ${song.title} to a playlist`}
                style={{ flex: '1 1 0', minWidth: 0 }}
              >
                <Bookmark size={13} />
                Save
              </Btn>
            </>
          )}
          {/* Share is on both surfaces — §9 lists it as part of the Library detail view. */}
          <Btn
            variant="secondary"
            onClick={() => onShare(song)}
            aria-label={`Share ${song.title}`}
            style={{ flex: '1 1 0', minWidth: 0 }}
          >
            <Share2 size={13} />
            Share
          </Btn>
          {/*
            THE EXPLICIT WAY INTO THE WORKSPACE, which is the other half of §9: a Library
            click must not open the Studio automatically, so the Studio becomes a named
            button you press on purpose. The same destination the row's More menu item has —
            AppModals routes both through `paths.song`, and RequireStudio on that route is
            what refuses it without the entitlement, exactly as it does for the menu item.

            Named "Open in Studio" to match that menu item and the Genre Mixer's card, so
            the app has one name for one action.
          */}
          {surface === 'library' && onOpenInStudio && (
            <Btn
              variant="secondary"
              onClick={() => onOpenInStudio(song)}
              aria-label={`Open ${song.title} in Lyrixsa Studio`}
              style={{ flex: '1 1 0', minWidth: 0 }}
            >
              <SlidersHorizontal size={13} />
              Open in Studio
            </Btn>
          )}
          {surface === 'library' && onOpenFullscreen && (
            <Btn
              variant="secondary"
              onClick={() => onOpenFullscreen(song)}
              aria-label={`Open ${song.title} in full screen`}
              style={{ flex: '1 1 0', minWidth: 0 }}
            >
              <Maximize2 size={13} />
              Full screen
            </Btn>
          )}
        </div>
      </div>
    </Modal>
  );
}
